/**
 * Ingame-Admins-Verwaltung (Issue #7, Slice 1). PID-basierte Ingame-Moderatoren,
 * GETRENNT vom Web-User-System. Gegatet durch `action.manage_ingame_admins`.
 * Liste + Editor (Preset/Permissions/Scope). Reine, getestete Helfer oben.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api } from "../store";
import { PageHead, Panel } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t, type TranslationKey } from "../i18n";
import {
  presetPermissions,
  roleForPermissions,
  type IngamePermission,
  type IngameRole,
} from "../../ingame/permissions";

/** Sichtbare Permission-Reihenfolge + i18n-Label-Key (für Checkboxen/Badges). */
export const INGAME_PERMISSION_LABELS: { perm: IngamePermission; key: TranslationKey; short: string }[] = [
  { perm: "action.ingame.warn", key: "ingameAdmins.permWarn", short: "warn" },
  { perm: "action.ingame.say", key: "ingameAdmins.permSay", short: "say" },
  { perm: "action.ingame.kick", key: "ingameAdmins.permKick", short: "kick" },
  { perm: "action.ingame.temp_ban", key: "ingameAdmins.permTempBan", short: "tban" },
  { perm: "action.ingame.pid_ban", key: "ingameAdmins.permPidBan", short: "pidban" },
  { perm: "action.ingame.autobalance", key: "ingameAdmins.permAutobalance", short: "ab" },
  { perm: "action.ingame.restart", key: "ingameAdmins.permRestart", short: "restart" },
  { perm: "action.ingame.next_map", key: "ingameAdmins.permNextMap", short: "nextmap" },
  { perm: "action.ingame.tickets", key: "ingameAdmins.permTickets", short: "tickets" },
  { perm: "action.ingame.fbf", key: "ingameAdmins.permFbf", short: "fbf" },
  { perm: "action.ingame.double_tickets", key: "ingameAdmins.permDoubleTickets", short: "x2" },
];

export const INGAME_ROLE_OPTIONS: { role: IngameRole; key: TranslationKey }[] = [
  { role: "helper", key: "ingameAdmins.roleHelper" },
  { role: "moderator", key: "ingameAdmins.roleModerator" },
  { role: "admin", key: "ingameAdmins.roleAdmin" },
  { role: "custom", key: "ingameAdmins.roleCustom" },
];

interface IngameAdminView {
  id: number;
  pid: string;
  label: string;
  role: IngameRole;
  enabled: boolean;
  scopeAll: boolean;
  servers: string[];
  permissions: IngamePermission[];
  notes: string;
  lastSeen: number;
  seenName: string;
}

interface Draft {
  orig: string;            // PID des bearbeiteten Eintrags ("" = neu)
  pid: string;
  label: string;
  role: IngameRole;
  enabled: boolean;
  scopeAll: boolean;
  servers: string[];
  permissions: IngamePermission[];
  notes: string;
}

/** Scope-Text für die Liste (i18n). */
export function scopeText(a: { scopeAll: boolean; servers: string[] }): string {
  return a.scopeAll ? t("ingameAdmins.allServers") : `${a.servers.length} ${t("ingameAdmins.nServersSuffix")}`;
}

/** „Zuletzt gesehen" aus Unix-Sekunden; 0 -> Dash. */
export function formatLastSeen(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  return new Date(sec * 1000).toLocaleDateString();
}

/** Nur Ziffern (UI-seitige PID-Normalisierung, identisch zum Store). */
export function pidDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function emptyDraft(): Draft {
  return { orig: "", pid: "", label: "", role: "helper", enabled: true, scopeAll: true, servers: [], permissions: presetPermissions("helper"), notes: "" };
}

function draftFrom(a: IngameAdminView): Draft {
  return { orig: a.pid, pid: a.pid, label: a.label, role: a.role, enabled: a.enabled, scopeAll: a.scopeAll, servers: [...a.servers], permissions: [...a.permissions], notes: a.notes };
}

export function IngameAdmins() {
  const admins = useSignal<IngameAdminView[]>([]);
  const allServers = useSignal<{ id: string; name: string }[]>([]);
  const draft = useSignal<Draft | null>(null);
  const edit = useSignal(false);

  async function load() {
    const r = await api.call<ApiResponse & { admins: IngameAdminView[]; servers: { id: string; name: string }[] }>("ingame_admins_list");
    if (r.ok) { admins.value = r.admins; allServers.value = r.servers; }
  }
  useSignalEffect(() => { void load(); });

  const newAdmin = () => (draft.value = emptyDraft());
  const editAdmin = (a: IngameAdminView) => (draft.value = draftFrom(a));

  async function save() {
    const d = draft.value;
    if (!d) return;
    const r = await api.call<ApiResponse>("ingame_admin_save", {
      pid: d.pid, label: d.label, role: d.role, enabled: d.enabled,
      scopeAll: d.scopeAll, servers: d.servers, permissions: d.permissions, notes: d.notes,
    });
    if (r.ok) { toast(t("ingameAdmins.toastSaved"), "ok"); draft.value = null; await load(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  async function del(a: IngameAdminView) {
    if (!confirm(`${t("ingameAdmins.confirmDeletePre")}${a.pid}${t("ingameAdmins.confirmDeletePost")}`)) return;
    const r = await api.call<ApiResponse>("ingame_admin_delete", { pid: a.pid });
    if (r.ok) { toast(t("ingameAdmins.toastDeleted"), "ok"); if (draft.value?.orig === a.pid) draft.value = null; await load(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  async function toggleEnabled(a: IngameAdminView) {
    const r = await api.call<ApiResponse>("ingame_admin_set_enabled", { pid: a.pid, enabled: !a.enabled });
    if (r.ok) { toast(a.enabled ? t("ingameAdmins.toastDisabled") : t("ingameAdmins.toastEnabled"), "ok"); await load(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  async function importLegacy() {
    if (!confirm(t("ingameAdmins.confirmImport"))) return;
    const r = await api.call<ApiResponse & { result?: { imported: number; total: number } }>("ingame_admins_import_legacy");
    if (r.ok) { toast(`${t("ingameAdmins.toastImported")} (+${r.result?.imported ?? 0}/${r.result?.total ?? 0})`, "ok"); await load(); }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  const d = draft.value;
  const setD = (patch: Partial<Draft>) => { if (d) draft.value = { ...d, ...patch }; };
  const selectRole = (role: IngameRole) => {
    if (!d) return;
    // Preset setzt die Permissions; custom lässt sie unverändert.
    draft.value = { ...d, role, permissions: role === "custom" ? d.permissions : presetPermissions(role) };
  };
  const togglePerm = (perm: IngamePermission) => {
    if (!d) return;
    const has = d.permissions.includes(perm);
    const permissions = has ? d.permissions.filter((p) => p !== perm) : [...d.permissions, perm];
    // Manuelle Änderung -> Rolle aus der Auswahl ableiten (Preset-Match oder custom).
    draft.value = { ...d, permissions, role: roleForPermissions(permissions) };
  };
  const toggleServer = (id: string) => {
    if (!d) return;
    const has = d.servers.includes(id);
    draft.value = { ...d, servers: has ? d.servers.filter((x) => x !== id) : [...d.servers, id] };
  };

  const items: PanelItem[] = [
    { id: "list", title: t("ingameAdmins.list"), defaultSpan: d ? 8 : 12, render: () => (
      <Panel title={t("ingameAdmins.list")} actions={<button class="btn sm ghost" title={t("ingameAdmins.importTitle")} onClick={() => void importLegacy()}>{t("ingameAdmins.import")}</button>}>
        {admins.value.length === 0 ? <div class="empty">{t("ingameAdmins.empty")}</div> : (
          <table class="score">
            <thead><tr>
              <th>PID</th><th>{t("ingameAdmins.label")}</th><th>{t("ingameAdmins.role")}</th>
              <th>{t("ingameAdmins.scope")}</th><th>{t("ingameAdmins.permissions")}</th>
              <th>{t("ingameAdmins.status")}</th><th>{t("ingameAdmins.lastSeen")}</th><th></th>
            </tr></thead>
            <tbody>
              {admins.value.map((a) => (
                <tr key={a.pid}>
                  <td class="mono accent-cell" data-label="PID">{a.pid}</td>
                  <td data-label={t("ingameAdmins.label")}>{a.label || a.seenName || "—"}</td>
                  <td data-label={t("ingameAdmins.role")}><span class={`badge ${a.role === "admin" ? "accent" : ""}`}>{a.role}</span></td>
                  <td class="muted" data-label={t("ingameAdmins.scope")}>{scopeText(a)}</td>
                  <td data-label={t("ingameAdmins.permissions")}>
                    <span class="row" style="gap:4px;flex-wrap:wrap">
                      {INGAME_PERMISSION_LABELS.filter((p) => a.permissions.includes(p.perm)).map((p) => (
                        <span key={p.perm} class="badge" style="font-size:10px">{p.short}</span>
                      ))}
                    </span>
                  </td>
                  <td data-label={t("ingameAdmins.status")}>
                    <span class={`badge ${a.enabled ? "ok" : ""}`}><span class={`live-dot ${a.enabled ? "open" : "offline"}`} /> {a.enabled ? t("ingameAdmins.active") : t("ingameAdmins.inactive")}</span>
                  </td>
                  <td class="num muted" data-label={t("ingameAdmins.lastSeen")}>{formatLastSeen(a.lastSeen)}</td>
                  <td class="num">
                    <button class="btn sm ghost" onClick={() => editAdmin(a)}>{t("common.editAction")}</button>
                    <button class="btn sm ghost" onClick={() => void toggleEnabled(a)}>{a.enabled ? t("ingameAdmins.disable") : t("ingameAdmins.enable")}</button>
                    <button class="btn sm ghost" onClick={() => void del(a)}>{t("common.delete")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    ) },
    ...(d ? [{ id: "editor", title: d.orig ? `PID ${d.orig} ${t("ingameAdmins.editSuffix")}` : t("ingameAdmins.new"), defaultSpan: 4, render: () => (
      <Panel title={d.orig ? `PID ${d.orig} ${t("ingameAdmins.editSuffix")}` : t("ingameAdmins.new")} actions={<button class="btn sm ghost" onClick={() => (draft.value = null)}>✕</button>}>
        <div class="stack">
          <div class="field"><label>PID</label>
            <input class="input mono" placeholder={t("ingameAdmins.pidPlaceholder")} value={d.pid} disabled={!!d.orig}
              onInput={(e) => setD({ pid: pidDigits((e.target as HTMLInputElement).value) })} /></div>
          <div class="field"><label>{t("ingameAdmins.label")}</label>
            <input class="input" placeholder={t("ingameAdmins.labelPlaceholder")} value={d.label}
              onInput={(e) => setD({ label: (e.target as HTMLInputElement).value })} /></div>
          <div class="field"><label>{t("ingameAdmins.rolePreset")}</label>
            <select class="select" value={d.role} onChange={(e) => selectRole((e.target as HTMLSelectElement).value as IngameRole)}>
              {INGAME_ROLE_OPTIONS.map((o) => <option key={o.role} value={o.role}>{t(o.key)}</option>)}
            </select>
          </div>
          <div class="field"><label>{t("ingameAdmins.permissions")}</label>
            <div class="stack" style="gap:6px">
              {INGAME_PERMISSION_LABELS.map((p) => (
                <label key={p.perm} class="row" style="gap:8px">
                  <input type="checkbox" checked={d.permissions.includes(p.perm)} onChange={() => togglePerm(p.perm)} />
                  {t(p.key)}
                </label>
              ))}
            </div>
          </div>
          <div class="field"><label>{t("ingameAdmins.scope")}</label>
            <label class="row" style="gap:8px">
              <input type="checkbox" checked={d.scopeAll} onChange={(e) => setD({ scopeAll: (e.target as HTMLInputElement).checked })} />
              {t("ingameAdmins.scopeAll")}
            </label>
            {!d.scopeAll && (
              <div class="stack" style="gap:6px;margin-top:6px">
                {allServers.value.map((s) => (
                  <label key={s.id} class="row" style="gap:8px">
                    <input type="checkbox" checked={d.servers.includes(s.id)} onChange={() => toggleServer(s.id)} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <label class="row" style="justify-content:space-between">
            <span>{t("ingameAdmins.enabled")}</span>
            <input type="checkbox" checked={d.enabled} onChange={(e) => setD({ enabled: (e.target as HTMLInputElement).checked })} />
          </label>
          <div class="field"><label>{t("ingameAdmins.notes")}</label>
            <textarea class="textarea" value={d.notes} onInput={(e) => setD({ notes: (e.target as HTMLTextAreaElement).value })} /></div>
          <button class="btn primary" onClick={() => void save()}>{t("common.save")}</button>
        </div>
      </Panel>
    ) }] as PanelItem[] : []),
  ];

  return (
    <div class="view-enter">
      <PageHead title={t("ingameAdmins.title")} sub={t("ingameAdmins.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        <button class="btn primary" onClick={newAdmin}>{t("ingameAdmins.add")}</button>
      </PageHead>
      <PanelGrid view="ingame-admins" items={items} edit={edit} />
    </div>
  );
}
